<script setup lang="ts">
// Repository guidance: repos where the code-map index found no agent instructions or no
// spec documents. Calmer than schedule alerts and not in the urgent count; admins can
// dismiss one, and the index reopens it only when a gap appears that they didn't see.
import { ref } from "vue";
import { useAuthStore } from "@/stores/auth";
import { useAlerts } from "@/composables/useAlerts";
import { GAP_LABEL, repoShort } from "@/lib/steering";
import { ago } from "@/lib/format";
import Icon from "@/components/ui/Icon.vue";

const auth = useAuthStore();
const { notices, resolve } = useAlerts();
const busy = ref<string | null>(null);
const error = ref("");
const expanded = ref(false);

async function dismiss(id: string) {
  busy.value = id;
  error.value = "";
  try { await resolve(id, false); }
  catch (e) { error.value = (e as Error).message || "Couldn't dismiss the notice."; }
  finally { busy.value = null; }
}
</script>

<template>
  <section v-if="notices.length" class="notices panel" aria-labelledby="notices-h">
    <header class="head">
      <p id="notices-h" class="eyebrow">Repository guidance · {{ notices.length }}</p>
      <p class="muted small">What the code map's nightly index found missing, at known paths. Presence only: a gap here says nothing about code quality.</p>
    </header>
    <ul class="list">
      <li v-for="n in (expanded ? notices : notices.slice(0, 3))" :key="n.id" class="item">
        <Icon name="file" class="ico" />
        <div class="body">
          <p class="line">
            <strong class="mono">{{ repoShort(n.repo) }}</strong>
            <span v-for="g in n.gaps" :key="g" class="badge" data-tone="attention">{{ GAP_LABEL[g] ?? g }}</span>
            <span class="muted small">since {{ ago(n.raised_at) }}</span>
          </p>
          <p class="muted small finding">{{ n.findings[0] }}</p>
        </div>
        <div class="actions">
          <RouterLink v-if="n.fix_url" :to="n.fix_url" class="btn btn--sm">On the map</RouterLink>
          <button v-if="auth.isAdmin" class="btn btn--sm btn--ghost" :disabled="busy === n.id" :aria-label="`Dismiss the notice for ${n.repo}`" @click="dismiss(n.id)">Dismiss</button>
        </div>
      </li>
    </ul>
    <button v-if="notices.length > 3" type="button" class="link" :aria-expanded="expanded" @click="expanded = !expanded">
      {{ expanded ? "Show fewer" : `Show all ${notices.length}` }}
    </button>
    <p v-if="error" class="inline-error" role="alert">{{ error }}</p>
  </section>
</template>

<style scoped>
.notices { padding: var(--space-5) var(--space-5); display: flex; flex-direction: column; gap: var(--space-3); }
.head { display: flex; flex-direction: column; gap: var(--space-2); }
.list { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; }
.item { display: flex; align-items: flex-start; gap: var(--space-4); padding: var(--space-3) 0; border-top: 1px solid var(--line-2); }
.ico { color: var(--muted); margin-top: var(--space-1); flex: none; }
.body { flex: 1; min-width: 0; }
.line { display: flex; align-items: center; gap: var(--space-3); flex-wrap: wrap; font-size: var(--text-sm); }
.finding { margin-top: var(--space-1); overflow-wrap: anywhere; }
.actions { display: flex; gap: var(--space-2); flex: none; }
.link { align-self: flex-start; border: 0; background: none; padding: 0; font-size: var(--text-xs); color: var(--brand); }
@media (max-width: 720px) {
  .item { flex-wrap: wrap; }
  .body { flex: 1 1 calc(100% - 40px); }
  .actions { margin-left: var(--space-6); }
}
</style>
